import {
  mapQueryClusterCards,
  mapQueryClusterTables,
  queryClusterCards,
  queryClusterTables
} from './queries'
import { defaultSortParams, getSortDirection, SortParams } from '../../utils/sortUtil'
import { CLUSTER_ID, DATABASE_ID, DATA_SOURCE_ID, PAGE, PAGE_SIZE } from '../../constants'
import graphqlService from '../../services/graphqlService'
import { RootState } from '../../rootReducer'
import { getGlobalParams } from '../../utils/urlUtil'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type ClusterCard = {
  databaseId: string
  databaseName: string
  clusterId: string
  clusterName: string
  tablesCount: number
  tablesPiiCount: number
  viewsPiiCount: number
  tablesPiiNeedReviewCount: number
  columnsPiiNeedReviewCount: number
  columnsCount: number
  columnsPiiCount: number
  viewColumnsPiiCount: number
  entitiesTotalCount: number
  entitiesScannedCount: number
  isReviewed: boolean
  isTimestampColumnConfigured?: boolean
  isView?: boolean
}

export type OrphanTablesCard = {
  tablesCount: number
  tablesPiiCount: number
  columnsCount: number
  columnsPiiCount: number
}

export interface ClusterCardsParams {
  [CLUSTER_ID]?: string
  [DATABASE_ID]?: string
  [DATA_SOURCE_ID]: string
}

export const ACTION_CLUSTER_CARDS = 'clusters/cards'
export const fetchClusterCards = createAsyncThunk(
  ACTION_CLUSTER_CARDS,
  async (params: ClusterCardsParams) => {
    const result = await graphqlService.execute(queryClusterCards(params))
    return mapQueryClusterCards(result)
  }
)

export type ClusterTable = {
  databaseId: string
  databaseName: string
  clusterId?: string
  clusterName?: string
  tableId: string
  tableName: string
  parentId: string
  rowsCount: number
  columnsCount: number
  columnsPiiCount: number
  noScanItemsCount: number
  schemaId: string
  schemaName: string
  lastModified: string
  isReviewed: boolean
  alertsCount?: number
}

export interface ClusterTablesParams {
  [PAGE]: number
  [PAGE_SIZE]?: number
  [CLUSTER_ID]: string
  [DATABASE_ID]: string
  [DATA_SOURCE_ID]: string
}

export const ACTION_CLUSTER_TABLES = 'clusters/tables'
export const fetchClusterTables = createAsyncThunk(
  ACTION_CLUSTER_TABLES,
  async (params: ClusterTablesParams, { getState }) => {
    const queryParams = { ...params, ...getGlobalParams(getState() as RootState) }
    const result = await graphqlService.execute(queryClusterTables(queryParams))
    return mapQueryClusterTables(result)
  }
)

export type ClusterTablesList = {
  list?: ClusterTable[]
  total?: number
  sort: SortParams
}

const initialList: ClusterTablesList = {
  sort: defaultSortParams
}

type ClustersState = {
  list: ClusterTablesList
  cards?: ClusterCard[]
  filters?: any
}

export const initialState: ClustersState = {
  list: initialList
}

const clustersSlice = createSlice({
  name: 'clusters',
  initialState,
  reducers: {
    setListSort: (state, { payload }) => {
      state.list.sort = getSortDirection(state.list.sort, payload.column)
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchClusterCards.fulfilled, (state, action) => {
      state.cards = action.payload
    })
    builder.addCase(fetchClusterTables.fulfilled, (state, { payload }) => {
      state.list.list = payload.list
      state.list.total = payload.total
    })
  }
})

export const { setListSort } = clustersSlice.actions

export default clustersSlice.reducer
